/* eslint-disable */

import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Bell, Megaphone, Send } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '../../Components/ui/button'
import { Card, CardContent } from '../../Components/ui/card'
import { Input } from '../../Components/ui/input'
import { Label } from '../../Components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../Components/ui/select'
import { Textarea } from '../../Components/ui/textarea'
import env from '../../env/env'
import { post } from '../../http/restservice'
import { getUserDetails } from '../../utils/AuthUtil'
import { cn } from '../../lib/utils'

const AUDIENCE_OPTIONS = [
  { value: 'INDIVIDUAL', label: 'Individual' },
  { value: 'LIST', label: 'List' },
  { value: 'BROADCAST', label: 'Broadcast' },
]

const IN_APP_TYPES = [
  { value: 'PERSONALISED', label: 'Personalised' },
  { value: 'GENERIC', label: 'Generic' },
]

const normalizePhone = (value) => {
  const trimmed = String(value || '').trim()
  if (!trimmed) return ''
  if (trimmed.startsWith('+')) return trimmed.replace(/\s+/g, '')
  const digits = trimmed.replace(/\D/g, '')
  if (!digits) return ''
  if (digits.startsWith('91') && digits.length === 12) return `+${digits}`
  if (digits.length === 10) return `+91${digits}`
  return `+${digits}`
}

const parsePhoneNumbers = (raw, audienceType) => {
  if (audienceType === 'BROADCAST') return []
  const values = String(raw || '')
    .split(/[\n,]+/)
    .map(normalizePhone)
    .filter(Boolean)
  return [...new Set(values)]
}

const phoneFieldHint = (audienceType) => {
  if (audienceType === 'INDIVIDUAL')
    return 'Enter one mobile number with country code, e.g. +919876543210'
  if (audienceType === 'LIST')
    return 'Paste numbers with +91, separated by commas. Example: +919876543210, +917503921723'
  return 'Broadcast reaches every eligible user. Phone numbers are not required.'
}

const getDefaultExpiryDate = (days = 7) => {
  const date = new Date()
  date.setDate(date.getDate() + days)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const getDefaultPhone = (locationState) => {
  try {
    const statePhone = locationState?.phoneNumber || locationState?.data?.phoneNumber
    if (statePhone) return normalizePhone(statePhone)
  } catch (e) {
    // ignore
  }

  try {
    const loginPhone = sessionStorage.getItem('actionPlanLoginPhone')
    if (loginPhone) return normalizePhone(loginPhone)
  } catch (e) {
    // ignore
  }

  try {
    const rawActionPlanUser = sessionStorage.getItem('actionPlanUser')
    if (rawActionPlanUser) {
      const parsed = JSON.parse(rawActionPlanUser)
      const customer = parsed?.data || parsed
      const phone =
        customer?.phoneNumber ||
        customer?.phone ||
        customer?.mobileNumber ||
        customer?.loginPhoneNumber
      if (phone) return normalizePhone(phone)
    }
  } catch (e) {
    // ignore
  }

  try {
    const userDetails = getUserDetails()
    const phone =
      userDetails?.phoneNumber ||
      userDetails?.phone_number ||
      userDetails?.phone ||
      (userDetails?.username && /^\+?\d{10,13}$/.test(userDetails.username)
        ? userDetails.username
        : null)
    if (phone) return normalizePhone(phone)
  } catch (e) {
    // ignore
  }

  return ''
}

const AdminNotifications = () => {
  const location = useLocation()
  const defaultPhone = useMemo(() => getDefaultPhone(location?.state), [location?.state])

  const [inApp, setInApp] = useState(() => ({
    message: '',
    imageUrl: '',
    deepLink: '',
    notificationType: 'PERSONALISED',
    audienceType: 'INDIVIDUAL',
    expiryDate: getDefaultExpiryDate(),
    phones: getDefaultPhone(location?.state),
  }))
  const [push, setPush] = useState(() => ({
    subject: '',
    content: '',
    imageUrl: '',
    deepLink: '',
    audienceType: 'INDIVIDUAL',
    phones: getDefaultPhone(location?.state),
  }))
  const [loading, setLoading] = useState({ inApp: false, push: false, both: false })

  useEffect(() => {
    if (defaultPhone) {
      setInApp((prev) => (prev.phones ? prev : { ...prev, phones: defaultPhone }))
      setPush((prev) => (prev.phones ? prev : { ...prev, phones: defaultPhone }))
    }
  }, [defaultPhone])

  const inAppPhones = useMemo(
    () => parsePhoneNumbers(inApp.phones, inApp.audienceType),
    [inApp.phones, inApp.audienceType],
  )
  const pushPhones = useMemo(
    () => parsePhoneNumbers(push.phones, push.audienceType),
    [push.phones, push.audienceType],
  )

  const validateAudience = (audienceType, phones, label) => {
    if (audienceType === 'BROADCAST') return true
    if (!phones.length) {
      toast.error(`Enter at least one phone number for ${label}.`)
      return false
    }
    if (audienceType === 'INDIVIDUAL' && phones.length !== 1) {
      toast.error(`Enter exactly one phone number for an individual ${label}.`)
      return false
    }
    return true
  }

  const sendInAppNotification = async (event) => {
    event.preventDefault()
    if (!inApp.message.trim()) {
      toast.error('Enter an in-app notification message.')
      return
    }
    if (!inApp.expiryDate) {
      toast.error('Choose an expiry date.')
      return
    }
    if (!validateAudience(inApp.audienceType, inAppPhones, 'in-app notification')) return

    setLoading((prev) => ({ ...prev, inApp: true }))
    try {
      const payload = {
        message: inApp.message.trim(),
        notificationType: inApp.notificationType,
        createdBy: 'Admin',
        audienceType: inApp.audienceType,
        expiryDate: inApp.expiryDate,
        ...(inApp.imageUrl.trim() && { imageUrl: inApp.imageUrl.trim() }),
        ...(inApp.deepLink.trim() && { deepLink: inApp.deepLink.trim() }),
      }
      if (inApp.audienceType !== 'BROADCAST') payload.phoneNumbers = inAppPhones

      const response = await post(`${env.API_URL}customerservice/api/notification`, payload)

      if (response?.isError) {
        toast.error(
          response?.response?.message ||
          response?.message ||
          'Failed to create in-app notification.',
        )
        return
      }

      toast.success(response?.message || 'In-app notification created.')
      setInApp((prev) => ({
        ...prev,
        message: '',
        imageUrl: '',
        deepLink: '',
        phones: prev.audienceType === 'INDIVIDUAL' ? defaultPhone : '',
        expiryDate: getDefaultExpiryDate(),
      }))
    } catch (error) {
      toast.error(error?.message || 'Failed to create in-app notification.')
    } finally {
      setLoading((prev) => ({ ...prev, inApp: false }))
    }
  }

  const sendPushNotification = async (event) => {
    event.preventDefault()
    if (!push.subject.trim()) {
      toast.error('Enter a push notification subject.')
      return
    }
    if (!push.content.trim()) {
      toast.error('Enter push notification content.')
      return
    }
    if (!validateAudience(push.audienceType, pushPhones, 'push notification')) return

    setLoading((prev) => ({ ...prev, push: true }))
    try {
      const payload = {
        subject: push.subject.trim(),
        content: push.content.trim(),
        audienceType: push.audienceType,
        ...(push.imageUrl.trim() && { imageUrl: push.imageUrl.trim() }),
        ...(push.deepLink.trim() && { deepLink: push.deepLink.trim() }),
      }
      if (push.audienceType !== 'BROADCAST') payload.phoneNumbers = pushPhones

      const response = await post(
        `${env.API_URL}customerservice/api/notification/sendPushNotification`,
        payload,
      )

      if (response?.isError) {
        toast.error(
          response?.response?.message || response?.message || 'Failed to send push notification.',
        )
        return
      }

      toast.success(response?.message || 'Push notification sent.')
      setPush((prev) => ({
        ...prev,
        subject: '',
        content: '',
        imageUrl: '',
        deepLink: '',
        phones: prev.audienceType === 'INDIVIDUAL' ? defaultPhone : '',
      }))
    } catch (error) {
      toast.error(error?.message || 'Failed to send push notification.')
    } finally {
      setLoading((prev) => ({ ...prev, push: false }))
    }
  }

  const sendBothNotifications = async (event) => {
    if (event?.preventDefault) event.preventDefault()

    const inAppMsg = inApp.message.trim() || push.content.trim()
    const pushContent = push.content.trim() || inAppMsg
    const pushSubject = push.subject.trim() || 'Fabits Notification'

    if (!inAppMsg) {
      toast.error('Enter a notification message.')
      return
    }

    if (!inApp.expiryDate) {
      toast.error('Choose an expiry date for the in-app notification.')
      return
    }

    const inAppAudiencePhones = inAppPhones.length ? inAppPhones : pushPhones
    const pushAudiencePhones = pushPhones.length ? pushPhones : inAppPhones

    if (!validateAudience(inApp.audienceType, inAppAudiencePhones, 'in-app notification')) {
      return
    }
    if (!validateAudience(push.audienceType, pushAudiencePhones, 'push notification')) {
      return
    }

    setLoading((prev) => ({ ...prev, both: true }))
    try {
      const inAppPayload = {
        message: inAppMsg,
        notificationType: inApp.notificationType,
        createdBy: 'Admin',
        audienceType: inApp.audienceType,
        expiryDate: inApp.expiryDate,
        ...(inApp.imageUrl.trim() && { imageUrl: inApp.imageUrl.trim() }),
        ...(inApp.deepLink.trim() && { deepLink: inApp.deepLink.trim() }),
      }
      if (inApp.audienceType !== 'BROADCAST') {
        inAppPayload.phoneNumbers = inAppAudiencePhones
      }

      const pushPayload = {
        subject: pushSubject,
        content: pushContent,
        audienceType: push.audienceType,
        ...(push.imageUrl.trim() && { imageUrl: push.imageUrl.trim() }),
        ...(push.deepLink.trim() && { deepLink: push.deepLink.trim() }),
      }
      if (push.audienceType !== 'BROADCAST') {
        pushPayload.phoneNumbers = pushAudiencePhones
      }

      const [inAppRes, pushRes] = await Promise.all([
        post(`${env.API_URL}customerservice/api/notification`, inAppPayload),
        post(`${env.API_URL}customerservice/api/notification/sendPushNotification`, pushPayload),
      ])

      const inAppOk = !inAppRes?.isError
      const pushOk = !pushRes?.isError

      if (inAppOk && pushOk) {
        toast.success('Both in-app and push notifications sent successfully!')
        setInApp((prev) => ({
          ...prev,
          message: '',
          imageUrl: '',
          deepLink: '',
          phones: prev.audienceType === 'INDIVIDUAL' ? defaultPhone : '',
          expiryDate: getDefaultExpiryDate(),
        }))
        setPush((prev) => ({
          ...prev,
          subject: '',
          content: '',
          imageUrl: '',
          deepLink: '',
          phones: prev.audienceType === 'INDIVIDUAL' ? defaultPhone : '',
        }))
      } else if (inAppOk && !pushOk) {
        toast.success(inAppRes?.message || 'In-app notification created.')
        toast.error(
          pushRes?.response?.message || pushRes?.message || 'Failed to send push notification.',
        )
        setInApp((prev) => ({
          ...prev,
          message: '',
          imageUrl: '',
          deepLink: '',
          phones: prev.audienceType === 'INDIVIDUAL' ? defaultPhone : '',
          expiryDate: getDefaultExpiryDate(),
        }))
      } else if (!inAppOk && pushOk) {
        toast.error(
          inAppRes?.response?.message ||
          inAppRes?.message ||
          'Failed to create in-app notification.',
        )
        toast.success(pushRes?.message || 'Push notification sent.')
        setPush((prev) => ({
          ...prev,
          subject: '',
          content: '',
          imageUrl: '',
          deepLink: '',
          phones: prev.audienceType === 'INDIVIDUAL' ? defaultPhone : '',
        }))
      } else {
        toast.error(
          inAppRes?.response?.message ||
          inAppRes?.message ||
          'Failed to create in-app notification.',
        )
        toast.error(
          pushRes?.response?.message || pushRes?.message || 'Failed to send push notification.',
        )
      }
    } catch (error) {
      toast.error(error?.message || 'Failed to send notifications.')
    } finally {
      setLoading((prev) => ({ ...prev, both: false }))
    }
  }

  return (
    <div className='mx-auto w-full max-w-none px-4 pb-8 pt-4 md:px-6 lg:px-8'>
      <div className='mb-5 flex flex-col gap-3 md:mb-6 md:flex-row md:items-center md:justify-between'>
        <div>
          <p className='mb-1 text-xs font-medium uppercase tracking-[0.08em] text-[var(--fb-text-muted)]'>
            Admin tools
          </p>
          <h1 className='font-IBM text-2xl font-bold tracking-tight text-[var(--fb-text-strong)] md:text-3xl'>
            Notifications
          </h1>
          <p className='mt-1 text-sm text-[var(--fb-text-muted)]'>
            Create in-app notifications and send push alerts to one user, a list, or everyone.
          </p>
        </div>

        <Button
          type='button'
          onClick={sendBothNotifications}
          disabled={loading.both || loading.inApp || loading.push}
          className={cn(
            'h-11 w-full gap-2 rounded-xl bg-[var(--fb-accent)] px-5 text-sm font-semibold text-white hover:bg-[var(--fb-accent-hover)] sm:w-auto',
            'disabled:bg-[var(--fb-accent)] disabled:opacity-50',
          )}
        >
          <Send className='h-4 w-4' aria-hidden='true' />
          {loading.both ? 'Sending both...' : 'Send both (In-app & Push)'}
        </Button>
      </div>

      <div className='grid w-full gap-4 md:gap-6 lg:grid-cols-2'>
        <Card className='overflow-hidden border border-[var(--fb-border)] bg-[var(--fb-surface)] shadow-[var(--fb-shadow-sm)] lg:rounded-3xl'>
          <CardContent className='p-5 md:p-8'>
            <div className='mb-5 flex items-start gap-3'>
              <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[var(--fb-accent-soft)] text-[var(--fb-accent-text)]'>
                <Bell className='h-6 w-6' aria-hidden='true' />
              </div>
              <div>
                <p className='text-sm font-semibold text-[var(--fb-text-strong)] md:text-base'>
                  In-app notification
                </p>
                <p className='mt-1 text-sm text-[var(--fb-text-muted)]'>
                  Shows inside the Fabits app. Choose personalised or market.
                </p>
              </div>
            </div>

            <form className='space-y-4' onSubmit={sendInAppNotification}>
              <div className='space-y-2'>
                <Label htmlFor='in-app-type'>Notification type</Label>
                <Select
                  value={inApp.notificationType}
                  onValueChange={(value) =>
                    setInApp((prev) => ({ ...prev, notificationType: value }))
                  }
                >
                  <SelectTrigger
                    id='in-app-type'
                    className='h-11 rounded-xl border-[var(--fb-border)]'
                  >
                    <SelectValue placeholder='Select type' />
                  </SelectTrigger>
                  <SelectContent>
                    {IN_APP_TYPES.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className='space-y-2'>
                <Label htmlFor='in-app-audience'>Audience</Label>
                <Select
                  value={inApp.audienceType}
                  onValueChange={(value) =>
                    setInApp((prev) => ({
                      ...prev,
                      audienceType: value,
                      phones:
                        value === 'INDIVIDUAL'
                          ? prev.phones || defaultPhone
                          : value === 'BROADCAST'
                            ? ''
                            : prev.phones,
                    }))
                  }
                >
                  <SelectTrigger
                    id='in-app-audience'
                    className='h-11 rounded-xl border-[var(--fb-border)]'
                  >
                    <SelectValue placeholder='Select audience' />
                  </SelectTrigger>
                  <SelectContent>
                    {AUDIENCE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {inApp.audienceType !== 'BROADCAST' && (
                <div className='space-y-2'>
                  <Label htmlFor='in-app-phones'>
                    {inApp.audienceType === 'LIST' ? 'Phone numbers' : 'Phone number'}
                  </Label>
                  {inApp.audienceType === 'LIST' ? (
                    <Textarea
                      id='in-app-phones'
                      value={inApp.phones}
                      onChange={(event) =>
                        setInApp((prev) => ({ ...prev, phones: event.target.value }))
                      }
                      placeholder='+919876543210, +917503921723'
                      className='min-h-[96px] rounded-xl border-[var(--fb-border)]'
                    />
                  ) : (
                    <Input
                      id='in-app-phones'
                      value={inApp.phones}
                      onChange={(event) =>
                        setInApp((prev) => ({ ...prev, phones: event.target.value }))
                      }
                      placeholder='+919876543210'
                      className='h-11 rounded-xl border-[var(--fb-border)]'
                    />
                  )}
                  <p className='text-xs text-[var(--fb-text-muted)]'>
                    {phoneFieldHint(inApp.audienceType)}
                    {inAppPhones.length > 0
                      ? ` · ${inAppPhones.length} number${inAppPhones.length === 1 ? '' : 's'
                      } ready`
                      : ''}
                  </p>
                </div>
              )}

              <div className='space-y-2'>
                <Label htmlFor='in-app-expiry'>Expiry date</Label>
                <Input
                  id='in-app-expiry'
                  type='date'
                  value={inApp.expiryDate}
                  onChange={(event) =>
                    setInApp((prev) => ({ ...prev, expiryDate: event.target.value }))
                  }
                  className='h-11 rounded-xl border-[var(--fb-border)]'
                />
              </div>

              <div className='space-y-2'>
                <Label htmlFor='in-app-message'>Message</Label>
                <Textarea
                  id='in-app-message'
                  value={inApp.message}
                  onChange={(event) =>
                    setInApp((prev) => ({ ...prev, message: event.target.value }))
                  }
                  placeholder='Write the in-app message'
                  className='min-h-[120px] rounded-xl border-[var(--fb-border)]'
                />
              </div>

              <div className='space-y-2'>
                <Label htmlFor='in-app-image-url'>Image URL (optional)</Label>
                <Input
                  id='in-app-image-url'
                  value={inApp.imageUrl}
                  onChange={(event) =>
                    setInApp((prev) => ({ ...prev, imageUrl: event.target.value }))
                  }
                  placeholder='https://example.com/notification.jpg'
                  className='h-11 rounded-xl border-[var(--fb-border)]'
                />
              </div>

              <div className='space-y-2'>
                <Label htmlFor='in-app-deep-link'>Deep link (optional)</Label>
                <Input
                  id='in-app-deep-link'
                  value={inApp.deepLink}
                  onChange={(event) =>
                    setInApp((prev) => ({ ...prev, deepLink: event.target.value }))
                  }
                  placeholder='/dashboard/firstTime/actionPlanUser'
                  aria-describedby='in-app-deep-link-hint'
                  className='h-11 rounded-xl border-[var(--fb-border)]'
                />
                <p id='in-app-deep-link-hint' className='text-xs text-[var(--fb-text-muted)]'>
                  Destination to open when the user taps the notification.
                </p>
              </div>

              <Button
                type='submit'
                disabled={loading.inApp || loading.both}
                className={cn(
                  'h-11 w-full gap-2 rounded-xl bg-[var(--fb-accent)] text-sm font-semibold text-white hover:bg-[var(--fb-accent-hover)]',
                  'disabled:bg-[var(--fb-accent)] disabled:opacity-50',
                )}
              >
                <Send className='h-4 w-4' aria-hidden='true' />
                {loading.inApp ? 'Creating...' : 'Create in-app notification'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className='overflow-hidden border border-[var(--fb-border)] bg-[var(--fb-surface)] shadow-[var(--fb-shadow-sm)] lg:rounded-3xl'>
          <CardContent className='p-5 md:p-8'>
            <div className='mb-5 flex items-start gap-3'>
              <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[var(--fb-accent-soft)] text-[var(--fb-accent-text)]'>
                <Megaphone className='h-6 w-6' aria-hidden='true' />
              </div>
              <div>
                <p className='text-sm font-semibold text-[var(--fb-text-strong)] md:text-base'>
                  Push notification
                </p>
                <p className='mt-1 text-sm text-[var(--fb-text-muted)]'>
                  Sends a device push to one user, a pasted list, or a full broadcast.
                </p>
              </div>
            </div>

            <form className='space-y-4' onSubmit={sendPushNotification}>
              <div className='space-y-2'>
                <Label htmlFor='push-audience'>Audience</Label>
                <Select
                  value={push.audienceType}
                  onValueChange={(value) =>
                    setPush((prev) => ({
                      ...prev,
                      audienceType: value,
                      phones:
                        value === 'INDIVIDUAL'
                          ? prev.phones || defaultPhone
                          : value === 'BROADCAST'
                            ? ''
                            : prev.phones,
                    }))
                  }
                >
                  <SelectTrigger
                    id='push-audience'
                    className='h-11 rounded-xl border-[var(--fb-border)]'
                  >
                    <SelectValue placeholder='Select audience' />
                  </SelectTrigger>
                  <SelectContent>
                    {AUDIENCE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {push.audienceType !== 'BROADCAST' && (
                <div className='space-y-2'>
                  <Label htmlFor='push-phones'>
                    {push.audienceType === 'LIST' ? 'Phone numbers' : 'Phone number'}
                  </Label>
                  {push.audienceType === 'LIST' ? (
                    <Textarea
                      id='push-phones'
                      value={push.phones}
                      onChange={(event) =>
                        setPush((prev) => ({ ...prev, phones: event.target.value }))
                      }
                      placeholder='+919876543210, +917378666101'
                      className='min-h-[96px] rounded-xl border-[var(--fb-border)]'
                    />
                  ) : (
                    <Input
                      id='push-phones'
                      value={push.phones}
                      onChange={(event) =>
                        setPush((prev) => ({ ...prev, phones: event.target.value }))
                      }
                      placeholder='+919876543210'
                      className='h-11 rounded-xl border-[var(--fb-border)]'
                    />
                  )}
                  <p className='text-xs text-[var(--fb-text-muted)]'>
                    {phoneFieldHint(push.audienceType)}
                    {pushPhones.length > 0
                      ? ` · ${pushPhones.length} number${pushPhones.length === 1 ? '' : 's'} ready`
                      : ''}
                  </p>
                </div>
              )}

              <div className='space-y-2'>
                <Label htmlFor='push-subject'>Subject</Label>
                <Input
                  id='push-subject'
                  value={push.subject}
                  onChange={(event) =>
                    setPush((prev) => ({ ...prev, subject: event.target.value }))
                  }
                  placeholder='Notification subject'
                  className='h-11 rounded-xl border-[var(--fb-border)]'
                />
              </div>

              <div className='space-y-2'>
                <Label htmlFor='push-content'>Content</Label>
                <Textarea
                  id='push-content'
                  value={push.content}
                  onChange={(event) =>
                    setPush((prev) => ({ ...prev, content: event.target.value }))
                  }
                  placeholder='Write the push notification content'
                  className='min-h-[120px] rounded-xl border-[var(--fb-border)]'
                />
              </div>

              <div className='space-y-2'>
                <Label htmlFor='push-image-url'>Image URL (optional)</Label>
                <Input
                  id='push-image-url'
                  value={push.imageUrl}
                  onChange={(event) =>
                    setPush((prev) => ({ ...prev, imageUrl: event.target.value }))
                  }
                  placeholder='https://example.com/notification.jpg'
                  className='h-11 rounded-xl border-[var(--fb-border)]'
                />
              </div>

              <div className='space-y-2'>
                <Label htmlFor='push-deep-link'>Deep link (optional)</Label>
                <Input
                  id='push-deep-link'
                  value={push.deepLink}
                  onChange={(event) =>
                    setPush((prev) => ({ ...prev, deepLink: event.target.value }))
                  }
                  placeholder='/dashboard/firstTime/actionPlanUser'
                  aria-describedby='push-deep-link-hint'
                  className='h-11 rounded-xl border-[var(--fb-border)]'
                />
                <p id='push-deep-link-hint' className='text-xs text-[var(--fb-text-muted)]'>
                  Destination to open when the user taps the notification.
                </p>
              </div>

              <Button
                type='submit'
                disabled={loading.push || loading.both}
                className={cn(
                  'h-11 w-full gap-2 rounded-xl bg-[var(--fb-accent)] text-sm font-semibold text-white hover:bg-[var(--fb-accent-hover)]',
                  'disabled:bg-[var(--fb-accent)] disabled:opacity-50',
                )}
              >
                <Send className='h-4 w-4' aria-hidden='true' />
                {loading.push ? 'Sending...' : 'Send push notification'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className='overflow-hidden border border-[var(--fb-border)] bg-[var(--fb-surface)] shadow-[var(--fb-shadow-sm)] lg:col-span-2 lg:rounded-3xl'>
          <CardContent className='flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between md:p-6'>
            <div className='flex items-center gap-3'>
              <div className='flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[var(--fb-accent-soft)] text-[var(--fb-accent-text)]'>
                <Send className='h-5 w-5' aria-hidden='true' />
              </div>
              <div>
                <p className='text-sm font-semibold text-[var(--fb-text-strong)] md:text-base'>
                  Send both notification types
                </p>
                <p className='mt-0.5 text-xs text-[var(--fb-text-muted)] md:text-sm'>
                  Dispatches both in-app notification and device push simultaneously.
                </p>
              </div>
            </div>
            <Button
              type='button'
              onClick={sendBothNotifications}
              disabled={loading.both || loading.inApp || loading.push}
              className={cn(
                'h-11 w-full shrink-0 gap-2 rounded-xl bg-[var(--fb-accent)] px-6 text-sm font-semibold text-white hover:bg-[var(--fb-accent-hover)] sm:w-auto',
                'disabled:bg-[var(--fb-accent)] disabled:opacity-50',
              )}
            >
              <Send className='h-4 w-4' aria-hidden='true' />
              {loading.both ? 'Sending both...' : 'Send both in-app & push'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default AdminNotifications
